import { Box, Typography } from '@mui/material';
import { MetricKey } from './seoTypes';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const SPARK = '▁▂▃▄▅▆▇█'; // ▁▂▃▄▅▆▇█

const UP = '#2e7d32';
const DOWN = '#c62828';
const MUTED = '#6b7280';

export const fmtInt = (n: number): string => Math.round(n).toLocaleString('en-US');

/** Avg position: one decimal; "—" when there are no impressions (v <= 0). */
export const fmtPos = (v: number): string => (v > 0 ? v.toFixed(1) : '—');

export const fmtMetric = (metric: MetricKey, v: number): string => {
    if (metric === 'ctr') return `${(v * 100).toFixed(2)}%`;
    if (metric === 'position') return fmtPos(v);
    return fmtInt(v);
};

/** Month label "2026-06" -> "Jun 2026" (or "Jun 2026 (MTD)" for the partial month). */
export const prettyMonth = (m: string, mtdMonth: string | null): string => {
    const [y, mm] = m.split('-');
    const s = `${MONTH_ABBR[Number(mm) - 1]} ${y}`;
    return m === mtdMonth ? `${s} (MTD)` : s;
};

/** Relative month-over-month change; null when prev is 0/absent. */
export const pct = (cur: number, prev: number | null | undefined): number | null =>
    prev == null || prev === 0 ? null : ((cur - prev) / prev) * 100;

/**
 * MoM delta chip. For avg position, `lowerBetter` inverts the color: a drop in
 * position is an improvement (green ▲ semantics), matching the Python renderer.
 */
export const Delta = ({ p, lowerBetter }: { p: number | null; lowerBetter?: boolean }) => {
    if (p == null) return null;
    if (Math.round(p) === 0) return null;
    const improved = lowerBetter ? p < 0 : p > 0;
    const color = improved ? UP : DOWN;
    const arrow = improved ? '▲' : '▼';
    return (
        <Typography component="span" variant="caption" sx={{ color, fontWeight: 600, ml: 0.75, whiteSpace: 'nowrap' }}>
            {arrow} {Math.abs(Math.round(p))}%
        </Typography>
    );
};

// Fixed width reserved for the delta so values stay column-aligned whether or
// not a delta is present.
const DELTA_SLOT = 54;

/**
 * A metric value + its MoM delta laid out in fixed columns: the value right-aligns
 * to a consistent edge, and the delta sits in a reserved slot beside it. This keeps
 * every value in a column at the same horizontal position and the green/red deltas
 * lined up, even when some rows have no delta.
 */
export const ValueDelta = ({
    text,
    p,
    lowerBetter,
    bold,
}: {
    text: string;
    p: number | null;
    lowerBetter?: boolean;
    bold?: boolean;
}) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', width: '100%' }}>
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: bold ? 700 : 400 }}>
            {text}
        </Box>
        <Box component="span" sx={{ width: DELTA_SLOT, flexShrink: 0, textAlign: 'left' }}>
            <Delta p={p} lowerBetter={lowerBetter} />
        </Box>
    </Box>
);

/**
 * Standalone delta cell (for a dedicated MoM column) — always renders something
 * (a muted "—" when there's no change/data) so the column reads consistently.
 */
export const DeltaCell = ({ p, lowerBetter }: { p: number | null; lowerBetter?: boolean }) => {
    if (p == null || Math.round(p) === 0) {
        return (
            <Typography component="span" variant="caption" sx={{ color: MUTED }}>
                —
            </Typography>
        );
    }
    const improved = lowerBetter ? p < 0 : p > 0;
    return (
        <Typography component="span" variant="caption" sx={{ color: improved ? UP : DOWN, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {improved ? '▲' : '▼'} {Math.abs(Math.round(p))}%
        </Typography>
    );
};

/** 8-level unicode bar sparkline over the full window (always 12+ months). */
export const Sparkline = ({ data }: { data: number[] }) => {
    const mx = Math.max(0, ...data);
    if (mx <= 0) {
        return (
            <Box component="span" sx={{ fontFamily: 'monospace', letterSpacing: '1px', color: '#cbd0d6' }}>
                {'·'.repeat(data.length)}
            </Box>
        );
    }
    const bars = data.map((v) => SPARK[Math.min(7, Math.floor((v / mx) * 7))]).join('');
    return (
        <Box component="span" sx={{ fontFamily: 'monospace', letterSpacing: '1px', color: '#4b5563' }}>
            {bars}
        </Box>
    );
};

export const colors = { UP, DOWN, MUTED };
